import React, { Component } from "react"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { Arwes, Button, Content, Frame as FrameC, Loading, Words } from "arwes"
import styled, { css, keyframes } from "styled-components"
import Icon from "@mdi/react"
import { mdiChemicalWeapon, mdiRefresh, mdiViewGrid } from "@mdi/js"
import history from "../../history"
import { formatAgo, formatAgoCompact } from "../../common/util/dates"
import UserAvatar from "../../common/UserAvatar"
import { MarginTop } from "../../common/styled/MarginTop"
import {
  ContentColumn,
  PageBlock,
  PageOffset
} from "../../common/styled/Page"
import { fetchGridPosts, fetchTrendingCells } from "./redux/reducers"

const SubHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 30px 20px;
  font-family: "VT323", monospace;
  font-size: 1.1rem;
  color: #6fbfff;

  @media (max-width: 900px) {
    margin: 0 12px 16px;
  }

  @media (max-width: 360px) {
    margin-left: 8px;
    margin-right: 8px;
  }
`

const SubHeadActions = styled.span`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 700px) {
    gap: 8px;
  }
`

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 24px;
  margin: 0 30px 30px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    margin: 0 12px 16px;
  }

  @media (max-width: 360px) {
    margin-left: 8px;
    margin-right: 8px;
  }
`

const FeedColumn = styled.div`
  min-width: 0;
`

const GridHeader = styled.h3`
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin: 10px 12px;
  text-align: center;

  @media (max-width: 700px) {
    gap: 4px 10px;
  }
`

const GridStats = styled.span`
  font-family: "VT323", monospace;
  font-size: 0.7em;
  letter-spacing: 0.1em;
  color: #ffb800;
`

const CardBody = styled.div`
  cursor: pointer;
`

// Forum-style card: author rail on the left, transmission on the right.
const CardRow = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  column-gap: 16px;
  padding: 14px 15px;

  @media (max-width: 700px) {
    grid-template-columns: 88px minmax(0, 1fr);
    column-gap: 12px;
    padding: 12px 12px 10px;
  }

  @media (max-width: 300px) {
    grid-template-columns: 64px minmax(0, 1fr);
    column-gap: 8px;
    padding: 10px 8px;
  }
`

const AuthorRail = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 96px;
  flex-shrink: 0;
  text-align: center;

  @media (max-width: 700px) {
    width: 88px;
    gap: 5px;
  }

  @media (max-width: 300px) {
    width: 64px;
  }
`

// UserAvatar has intentionally self-contained inline dimensions. This wrapper
// is the responsive contract for the Grid's desktop 72px / mobile 64px rail.
const AuthorAvatar = styled.span`
  display: flex;

  @media (max-width: 700px) {
    > img {
      width: 64px !important;
      height: 64px !important;
    }
  }

  @media (max-width: 300px) {
    > img {
      width: 56px !important;
      height: 56px !important;
    }
  }
`

const AuthorName = styled.div`
  font-family: "VT323", monospace;
  font-size: 0.85rem;
  color: #00e7ff;
  word-break: break-word;

  @media (max-width: 700px) {
    font-size: 14px;
  }
`

const PostAgo = styled.div`
  font-family: "VT323", monospace;
  font-size: 0.75rem;
  color: #d1ffdb;

  @media (max-width: 700px) {
    font-size: 11px;
    line-height: 1.1;
  }
`

const MsgCount = styled.div`
  font-family: "VT323", monospace;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: #ffb800;

  @media (max-width: 700px) {
    font-size: 12px;
  }
`

const CardMain = styled.div`
  grid-column: 2;
  grid-row: 1;
  min-width: 0;

  blockquote {
    overflow-wrap: anywhere;
  }

  @media (max-width: 700px) {
    blockquote {
      margin: 0;
      padding-left: 10px;
      border-left-width: 3px;
      font-size: 15px;
    }
  }
`

const ChipRow = styled.div`
  padding: 0 0 8px;
`

const DesktopOnly = styled.span`
  @media (max-width: 700px) {
    display: none;
  }
`

const MobileOnly = styled.span`
  display: none;

  @media (max-width: 700px) {
    display: inline;
  }
`

/* ---- Thread Pulse (design: grid-replies-preview-enhancement, option 1C) ---- */

const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`

const pulseBarLayout = css`
  grid-column: 2;
  margin-top: 14px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  border-top: 1px dashed rgba(190, 38, 252, 0.25);
  padding: 10px 4px 6px;
  text-align: left;

  @media (max-width: 700px) {
    grid-column: 1 / -1;
    margin-top: 12px;
    min-height: 44px;
    gap: 9px;
    padding: 8px 2px 2px;
  }
`

const PulseBar = styled.button`
  ${pulseBarLayout}
  appearance: none;
  border-right: 0;
  border-bottom: 0;
  border-left: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  touch-action: manipulation;

  &:hover {
    background: rgba(190, 38, 252, 0.06);
  }

  &:focus {
    outline: 1px solid rgba(0, 231, 255, 0.75);
    outline-offset: 2px;
  }
`

const ZeroPulseBar = styled(PulseBar)`
  &:hover {
    background: rgba(23, 255, 14, 0.04);
  }
`

const FacePile = styled.span`
  display: inline-flex;
  flex: none;

  > span {
    display: flex;
    position: relative;
  }
  > span:first-child {
    z-index: 2;
  }
  > span + span {
    margin-left: -9px;
  }

  @media (max-width: 700px) {
    > span + span {
      margin-left: -8px;
    }

    img {
      width: 24px !important;
      height: 24px !important;
    }
  }
`

const PulseDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex: none;
  background: ${p => (p.dim ? "#3a4670" : "#00e7ff")};
  box-shadow: ${p => (p.dim ? "none" : "0 0 6px #00e7ff")};
  ${p =>
    !p.dim &&
    css`
      animation: ${pulseDot} 1.6s infinite;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const PulseCount = styled.span`
  font-family: "VT323", monospace;
  font-size: 14px;
  color: #00e7ff;
  letter-spacing: 1px;
  flex: none;

  @media (max-width: 700px) {
    font-size: 15px;
  }
`

const PulseMeta = styled.span`
  font-family: "VT323", monospace;
  font-size: 14px;
  color: #6d7da6;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`

const PulseCaret = styled.span`
  font-family: "VT323", monospace;
  font-size: 13px;
  color: #be26fc;
  letter-spacing: 1px;
  flex: none;
  margin-left: auto;

  @media (max-width: 700px) {
    font-size: 16px;
    letter-spacing: 0;
  }
`

const ZeroLabel = styled.span`
  font-family: "VT323", monospace;
  font-size: 14px;
  color: #3a4670;
  letter-spacing: 1px;
`

const interactiveTextReset = css`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  font-family: "VT323", monospace;
  cursor: pointer;
  touch-action: manipulation;

  &:focus {
    outline: 1px solid currentColor;
    outline-offset: 2px;
  }
`

const ZeroAction = styled.span`
  font-family: "VT323", monospace;
  font-size: 13px;
  color: #17ff0e;
  flex: none;
  margin-left: auto;

  @media (max-width: 700px) {
    font-size: 14px;
  }
`

const EchoPanel = styled.div`
  grid-column: 2;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  margin-top: 10px;
  background: rgba(8, 4, 40, 0.85);
  border: 1px solid rgba(190, 38, 252, 0.5);
  padding: 4px 14px 6px;

  @media (max-width: 700px) {
    grid-column: 1 / -1;
    margin-top: 8px;
    padding: 2px 12px 0;
  }
`

const EchoRow = styled.div`
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  grid-template-areas: "avatar identity text right";
  align-items: center;
  column-gap: 10px;
  padding: 9px 0;

  & + & {
    border-top: 1px dashed rgba(190, 38, 252, 0.18);
  }

  @media (max-width: 700px) {
    grid-template-columns: 24px minmax(0, 1fr) auto;
    grid-template-areas:
      "avatar identity right"
      ". text text";
    column-gap: 9px;
    row-gap: 4px;
  }
`

const EchoAvatar = styled.span`
  grid-area: avatar;
  display: flex;

  @media (max-width: 700px) {
    > img {
      width: 24px !important;
      height: 24px !important;
    }
  }
`

const EchoIdentity = styled.span`
  grid-area: identity;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 700px) {
    gap: 9px;
  }
`

const EchoName = styled.span`
  font-family: "VT323", monospace;
  font-size: 14px;
  color: #00e7ff;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EchoOpPill = styled.span`
  font-family: "VT323", monospace;
  font-size: 11px;
  letter-spacing: 1px;
  color: #fc26fa;
  border: 1px solid currentColor;
  padding: 0 5px;
  flex: none;
`

const EchoText = styled.span`
  grid-area: text;
  font-size: 14px;
  color: #d6e1ff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  @media (max-width: 700px) {
    white-space: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-height: 1.35;
  }
`

const EchoTime = styled.span`
  font-family: "VT323", monospace;
  font-size: 12px;
  color: #6d7da6;
  text-transform: uppercase;
  flex: none;
  white-space: nowrap;
`

const EchoIndex = styled.span`
  font-family: "VT323", monospace;
  font-size: 12px;
  color: #3a4670;
  flex: none;

  @media (max-width: 700px) {
    display: none;
  }
`

const EchoRight = styled.span`
  grid-area: right;
  display: inline-flex;
  align-items: center;
  gap: 10px;
`

const EchoFooter = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  border-top: 1px dashed rgba(190, 38, 252, 0.18);
  padding: 9px 0 6px;

  @media (max-width: 700px) {
    padding: 0;
  }
`

const EchoAction = styled.button`
  ${interactiveTextReset}
  font-size: 13px;
  letter-spacing: 1px;
  min-height: 24px;

  @media (max-width: 700px) {
    flex: 1 1 50%;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
`

const ReplyAction = styled(EchoAction)`
  color: #17ff0e;

  &:hover {
    text-shadow: 0 0 8px rgba(23, 255, 14, 0.8);
  }
`

const ViewThread = styled(EchoAction)`
  color: #fc26fa;

  &:hover {
    text-shadow: 0 0 8px rgba(252, 38, 250, 0.8);
  }

  @media (max-width: 700px) {
    border-left: 1px solid rgba(190, 38, 252, 0.18);
  }
`

// How many of the latest replies the expanded pulse panel shows.
const PULSE_MAX_ECHOES = 3

const CellChip = styled(Link)`
  display: inline-block;
  box-sizing: border-box;
  max-width: 100%;
  font-family: "VT323", monospace;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  color: #17ff0e;
  border: 1px solid rgba(23, 255, 14, 0.5);
  padding: 2px 8px;
  text-decoration: none;
  overflow-wrap: anywhere;
  word-break: break-word;

  &:hover {
    color: #00e7ff;
    border-color: rgba(0, 231, 255, 0.7);
  }

  @media (max-width: 700px) {
    font-size: 12px;
    padding: 2px 7px;
  }
`

const Title = styled.div`
  padding: 0 10px 8px 0;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: 700px) {
    font-family: "Electrolize", sans-serif;
    font-size: 20px;
    color: #be26fc;
    padding-right: 0;
  }
`

const GridPostWrap = styled.div`
  margin-bottom: 8px;

  @media (max-width: 700px) {
    margin-bottom: 14px;
  }
`

const RailTitle = styled.div`
  font-family: "VT323", monospace;
  letter-spacing: 0.12em;
  color: #ffb800;
  padding: 12px 14px 6px;
`

const RailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  padding: 6px 14px;
  font-family: "VT323", monospace;
  font-size: 0.95rem;
  cursor: pointer;

  a {
    min-width: 0;
    color: #00e7ff;
    text-decoration: none;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  a:hover {
    color: #17ff0e;
  }

  span {
    color: #6fbfff;
    white-space: nowrap;
  }
`

const PagingFooter = styled.div`
  text-align: center;
  margin: 20px 0 10px;
  font-family: "VT323", monospace;
  color: #6fbfff;

  @media (max-width: 700px) {
    margin: 4px 0 0;
    font-size: 14px;
    letter-spacing: 2px;
  }
`

class GridPost extends Component {
  state = { layer: "success", corner: 1, pulseOpen: false }

  mouseOver = () => this.setState({ layer: "control", corner: 4 })
  mouseLeave = () => this.setState({ layer: "success", corner: 1 })

  togglePulse = e => {
    e.stopPropagation()
    this.setState(prev => ({ pulseOpen: !prev.pulseOpen }))
  }

  openThread = e => {
    e.stopPropagation()
    history.push(`/post/${this.props.post._id}`)
  }

  openReply = e => {
    e.stopPropagation()
    history.push(`/post/${this.props.post._id}#reply`)
  }

  // Thread Pulse (design option 1C): facepile bar that expands the
  // latest replies in-feed — clicking it never navigates.
  renderPulse() {
    const { post } = this.props
    const replies = post.comments || []

    if (replies.length === 0) {
      return (
        <ZeroPulseBar type="button" onClick={this.openReply}>
          <PulseDot dim aria-hidden="true" />
          <ZeroLabel>
            <DesktopOnly>NO SIGNAL ON THIS THREAD</DesktopOnly>
            <MobileOnly>NO SIGNAL YET</MobileOnly>
          </ZeroLabel>
          <ZeroAction>
            <DesktopOnly>◢ TRANSMIT FIRST REPLY</DesktopOnly>
            <MobileOnly>◢ TRANSMIT ▸</MobileOnly>
          </ZeroAction>
        </ZeroPulseBar>
      )
    }

    const last = replies[replies.length - 1]
    // Latest repliers, unique, newest first — max two faces.
    const seen = {}
    const faces = []
    for (let i = replies.length - 1; i >= 0 && faces.length < 2; i--) {
      const by = replies[i].postedBy
      const id = by && by._id ? String(by._id) : "anon"
      if (!seen[id]) {
        seen[id] = true
        faces.push(by)
      }
    }
    const opId = post.postedBy && post.postedBy._id
    const shown = replies.slice(-PULSE_MAX_ECHOES)
    const baseIndex = replies.length - shown.length
    const panelId = `grid-pulse-${post._id}`

    return (
      <React.Fragment>
        <PulseBar
          type="button"
          onClick={this.togglePulse}
          aria-expanded={this.state.pulseOpen}
          aria-controls={panelId}
          aria-label={`${replies.length} ${
            replies.length === 1 ? "reply" : "replies"
          }; ${this.state.pulseOpen ? "collapse" : "expand"} preview`}
        >
          <FacePile aria-hidden="true">
            {faces.map((by, i) => (
              <span key={(by && by._id) || i}>
                <UserAvatar user={by} size={26} square />
              </span>
            ))}
          </FacePile>
          <PulseDot aria-hidden="true" />
          <PulseCount>
            {replies.length} {replies.length === 1 ? "REPLY" : "REPLIES"}
          </PulseCount>
          <PulseMeta>
            <DesktopOnly>· LAST SIGNAL {formatAgo(last.created)}</DesktopOnly>
            <MobileOnly>· {formatAgoCompact(last.created)}</MobileOnly>
          </PulseMeta>
          <PulseCaret>
            <DesktopOnly>
              {this.state.pulseOpen ? "COLLAPSE ▴" : "EXPAND ▾"}
            </DesktopOnly>
            <MobileOnly>{this.state.pulseOpen ? "▴" : "▾"}</MobileOnly>
          </PulseCaret>
        </PulseBar>

        {this.state.pulseOpen && (
          <EchoPanel id={panelId} onClick={e => e.stopPropagation()}>
            {shown.map((reply, i) => {
              const by = reply.postedBy
              const isOp =
                opId && by && String(by._id) === String(opId)
              return (
                <EchoRow key={reply._id || i}>
                  <EchoAvatar>
                    <UserAvatar user={by} size={28} square />
                  </EchoAvatar>
                  <EchoIdentity>
                    <EchoName>{(by && by.name) || "deletedUser"}</EchoName>
                    {isOp && <EchoOpPill>OP</EchoOpPill>}
                  </EchoIdentity>
                  <EchoText>{reply.text}</EchoText>
                  <EchoRight>
                    <EchoTime>
                      <DesktopOnly>{formatAgo(reply.created)}</DesktopOnly>
                      <MobileOnly>
                        {formatAgoCompact(reply.created)}
                      </MobileOnly>
                    </EchoTime>
                    <EchoIndex>
                      #{String(baseIndex + i + 1).padStart(2, "0")}
                    </EchoIndex>
                  </EchoRight>
                </EchoRow>
              )
            })}
            <EchoFooter>
              <ReplyAction type="button" onClick={this.openReply}>
                ◢ REPLY
              </ReplyAction>
              <ViewThread type="button" onClick={this.openThread}>
                <DesktopOnly>VIEW FULL THREAD ▸</DesktopOnly>
                <MobileOnly>FULL THREAD ▸</MobileOnly>
              </ViewThread>
            </EchoFooter>
          </EchoPanel>
        )}
      </React.Fragment>
    )
  }

  render() {
    const { post } = this.props
    const cell = post.cellId && post.cellId.name ? post.cellId : null
    const ago = formatAgo(post.created)

    return (
      <GridPostWrap>
        <FrameC
          onMouseOver={this.mouseOver}
          onMouseLeave={this.mouseLeave}
          animate
          level={2}
          corners={this.state.corner}
          layer={this.state.layer}
        >
          <CardBody onClick={() => history.push(`/post/${post._id}`)}>
            <Content node="div">
              <CardRow>
                <AuthorRail>
                  <AuthorAvatar>
                    <UserAvatar user={post.postedBy} size={72} square />
                  </AuthorAvatar>
                  <AuthorName>
                    {post.postedBy ? post.postedBy.name : "deletedUser"}
                  </AuthorName>
                  {post.postedBy && post.postedBy.messageCount != null && (
                    <MsgCount>MSGS: {post.postedBy.messageCount}</MsgCount>
                  )}
                  <PostAgo>{ago}</PostAgo>
                </AuthorRail>
                <CardMain>
                  {cell && (
                    <ChipRow>
                      <CellChip
                        to={`/cell/${cell._id}`}
                        onClick={e => e.stopPropagation()}
                      >
                        #{cell.name}
                      </CellChip>
                    </ChipRow>
                  )}
                  <Title data-layer="disabled">{post.title}</Title>
                  <blockquote data-layer="disabled">
                    {(post.body || "").substring(0, 100)}
                  </blockquote>
                </CardMain>
                {this.renderPulse()}
              </CardRow>
            </Content>
          </CardBody>
        </FrameC>
      </GridPostWrap>
    )
  }
}

class TheGrid extends Component {
  state = { show: false }

  componentDidMount() {
    // Fetch on first visit only — navigating back reuses the store.
    // Refresh button (and sign-out reset) cover staleness.
    if (!this.props.loaded) {
      this.refresh()
    }
    setTimeout(() => this.setState({ show: true }), 600)
  }

  refresh = () => {
    this.props.fetchGridPosts(1)
    this.props.fetchTrendingCells()
  }

  loadMore = () => {
    const { page, isLoadingMore } = this.props
    if (!isLoadingMore) {
      this.props.fetchGridPosts(page + 1)
    }
  }

  renderError() {
    return (
      <PageBlock>
        <FrameC animate level={3} corners={3} layer="alert">
          <div
            style={{
              padding: 24,
              textAlign: "center",
              fontFamily: "VT323, monospace"
            }}
          >
            <div style={{ color: "#ff001c", marginBottom: 14 }}>
              SIGNAL LOST // the grid could not be reached
            </div>
            <Button animate layer="success" onClick={() => this.props.fetchGridPosts(1)}>
              <Icon path={mdiChemicalWeapon} size={0.5} color="#17ff0e" spin />{" "}
              RETRY
            </Button>
          </div>
        </FrameC>
      </PageBlock>
    )
  }

  renderEmpty() {
    return (
      <PageBlock>
        <FrameC animate level={3} corners={3} layer="alert">
          <div
            style={{
              padding: 24,
              textAlign: "center",
              fontFamily: "VT323, monospace"
            }}
          >
            <div style={{ color: "#ff001c", marginBottom: 14 }}>
              NO SIGNAL // join a cell to light up the grid
            </div>
            <Link to="/cells" style={{ textDecoration: "none" }}>
              <Button animate layer="success">
                <Icon path={mdiViewGrid} size={0.5} color="#17ff0e" /> Browse
                Cells
              </Button>
            </Link>
          </div>
        </FrameC>
      </PageBlock>
    )
  }

  renderRail() {
    const { cells, trending } = this.props
    return (
      <div>
        {cells && cells.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <FrameC show={this.state.show} animate level={3} corners={3} layer="primary">
              <div style={{ paddingBottom: 10 }}>
                <RailTitle>YOUR CELLS</RailTitle>
                {cells.map(c => (
                  <RailRow key={c._id}>
                    <Link to={`/cell/${c._id}`}>#{c.name}</Link>
                    <span>{c.postCount}</span>
                  </RailRow>
                ))}
              </div>
            </FrameC>
          </div>
        )}
        {trending && trending.length > 0 && (
          <FrameC show={this.state.show} animate level={3} corners={3} layer="success">
            <div style={{ paddingBottom: 10 }}>
              <RailTitle>TRENDING · 7D</RailTitle>
              {trending.map(c => (
                <RailRow key={c._id}>
                  <Link to={`/cell/${c._id}`}>#{c.name}</Link>
                  <span>+{c.postCount} POSTS</span>
                </RailRow>
              ))}
            </div>
          </FrameC>
        )}
      </div>
    )
  }

  render() {
    const {
      posts,
      cells,
      page,
      total,
      hasMore,
      isLoading,
      isLoadingMore,
      isError,
      isExpanded
    } = this.props
    const noCells = !isLoading && !isError && (!cells || cells.length === 0)

    return (
      <Arwes>
        <PageOffset isExpanded={isExpanded}>
          <MarginTop />
          <ContentColumn maxWidth={1080}>
            <PageBlock>
              <FrameC
                show={this.state.show}
                animate
                level={2}
                corners={4}
                layer="alert"
              >
                <GridHeader>
                  <Words animate layer="alert">
                    THE GRID
                  </Words>
                  <GridStats>
                    {(cells || []).length} CELLS · {total} TRANSMISSIONS
                  </GridStats>
                </GridHeader>
              </FrameC>
            </PageBlock>

            <SubHead>
              <span>&gt; transmissions from your joined cells · newest first</span>
              <SubHeadActions>
                <Button animate layer="control" onClick={this.refresh}>
                  <Icon path={mdiRefresh} size={0.6} color="#00e7ff" /> Refresh
                </Button>
                <Link to="/cells" style={{ textDecoration: "none" }}>
                  <Button animate layer="primary">
                    <Icon path={mdiViewGrid} size={0.6} color="#be26fc" /> Browse
                    Cells
                  </Button>
                </Link>
              </SubHeadActions>
            </SubHead>

            {isLoading && (
              <div style={{ margin: "30px", textAlign: "center" }}>
                <Loading animate small />
              </div>
            )}

            {!isLoading && isError && this.renderError()}

            {noCells && this.renderEmpty()}

            {!isLoading && !isError && !noCells && (
              <Columns>
                <FeedColumn>
                  {(!posts || posts.length === 0) && (
                    <FrameC animate level={3} corners={3} layer="disabled">
                      <div
                        style={{
                          padding: 24,
                          textAlign: "center",
                          color: "#6fbfff",
                          fontFamily: "VT323, monospace"
                        }}
                      >
                        No transmissions yet — post something in one of your
                        cells.
                      </div>
                    </FrameC>
                  )}
                  {posts &&
                    posts.map(p => <GridPost key={p._id} post={p} />)}

                  {posts && posts.length > 0 && (
                    <PagingFooter>
                      {hasMore && (
                        <div style={{ marginBottom: 10 }}>
                          <Button
                            animate
                            layer="success"
                            onClick={this.loadMore}
                            disabled={isLoadingMore}
                          >
                            <Icon
                              path={mdiChemicalWeapon}
                              size={0.5}
                              color="#17ff0e"
                              spin={isLoadingMore}
                            />{" "}
                            LOAD 20 MORE ▼
                          </Button>
                        </div>
                      )}
                      <div>
                        PAGE {page} · {total} TRANSMISSIONS TOTAL
                      </div>
                    </PagingFooter>
                  )}
                </FeedColumn>
                {this.renderRail()}
              </Columns>
            )}
          </ContentColumn>
        </PageOffset>
      </Arwes>
    )
  }
}

const mapState = state => ({
  posts: state.grid.posts,
  page: state.grid.page,
  total: state.grid.total,
  hasMore: state.grid.hasMore,
  cells: state.grid.cells,
  trending: state.grid.trending,
  isLoading: state.grid.isLoading,
  isLoadingMore: state.grid.isLoadingMore,
  isError: state.grid.isError,
  loaded: state.grid.loaded
})

export default connect(
  mapState,
  { fetchGridPosts, fetchTrendingCells }
)(TheGrid)
